// `LocaleToggle` — FR / EN segmented switch for the auth pages.
//
// DESIGN.md §0.1 + O5: anchored top-right of the form panel. Implemented as
// a `role="radiogroup"` (per a11y baselines) using DS `Button` ghost variants
// so the visual treatment ties into existing tokens without a DS-gap.

import { useLocale, useSetLocale, useT } from '@barel/i18n';
import type { Locale } from '@barel/i18n';
import { Button } from '@barel/ui';

export interface LocaleToggleProps {
  /** Optional className passthrough so parents can position the toggle. */
  className?: string;
}

const LOCALES: readonly Locale[] = ['fr', 'en'];

export function LocaleToggle({ className }: LocaleToggleProps) {
  const t = useT('auth');
  const locale = useLocale();
  const setLocale = useSetLocale();

  return (
    <div
      role="radiogroup"
      aria-label={t('layout.localeTogglerLabel')}
      data-region="auth-locale-toggle"
      className={['inline-flex items-center gap-1', className].filter(Boolean).join(' ')}
    >
      {LOCALES.map((value) => {
        const selected = locale === value;
        const label = value === 'fr' ? t('layout.localeFr') : t('layout.localeEn');
        return (
          <Button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            variant={selected ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => {
              setLocale(value);
            }}
            data-locale={value}
          >
            {label}
          </Button>
        );
      })}
    </div>
  );
}
